import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform } from "react-native";
import type { TextInput, View } from "react-native";

import { useToast } from "../../../../../contexts/Toast/ToastProvider";

export type PresetSnoozeOption = "1 hour" | "1 week";

export const CUSTOM_SNOOZE_OPTION = "Custom...";
export const CUSTOM_SNOOZE_WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
export const BANNER_TRANSITION_DURATION_MS = 200;
export const OPTIONS_TRANSITION_DURATION_MS = 150;

const MAX_SNOOZE_DAYS_AHEAD = 30;
const DEFAULT_CUSTOM_SNOOZE_HOUR = "12";
const DEFAULT_CUSTOM_SNOOZE_MINUTE = "00";

const getStartOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const getStartOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

export const isSameDay = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const getCalendarDaysForMonth = (monthDate: Date) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDayOffset = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const totalCells = Math.ceil((firstDayOffset + daysInMonth) / 7) * 7;

  return Array.from({ length: totalCells }, (_, index) => {
    const dayNumber = index - firstDayOffset + 1;
    if (dayNumber < 1 || dayNumber > daysInMonth) {
      return null;
    }

    return new Date(year, month, dayNumber);
  });
};

const normalizeTimeSegment = (value: string) => value.replace(/\D/g, "").slice(0, 2);

const parseCustomSnoozeTime = (hoursValue: string, minutesValue: string) => {
  if (hoursValue.length !== 2 || minutesValue.length !== 2) {
    return null;
  }

  const hours = Number.parseInt(hoursValue, 10);
  const minutes = Number.parseInt(minutesValue, 10);

  if (Number.isNaN(hours) || Number.isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return { hours, minutes };
};

const formatSnoozeUntilLabel = (date: Date) =>
  date.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

type UseSnoozeStateParams = {
  animationsEnabled: boolean;
  snoozeAllUntil: string | null;
  onUserInteraction: () => void;
  onPresetSnoozeSelect: (option: PresetSnoozeOption) => Promise<void>;
  onCustomSnoozeSelect: (snoozeUntilIso: string) => Promise<boolean>;
  onResumeSnooze: () => Promise<void>;
};

export const useSnoozeState = ({
  animationsEnabled,
  snoozeAllUntil,
  onUserInteraction,
  onPresetSnoozeSelect,
  onCustomSnoozeSelect,
  onResumeSnooze,
}: UseSnoozeStateParams) => {
  const { showToast } = useToast();
  const [showCustomSnooze, setShowCustomSnooze] = useState(false);
  const [customSnoozeDate, setCustomSnoozeDate] = useState<Date | null>(null);
  const [customSnoozeHours, setCustomSnoozeHours] = useState(DEFAULT_CUSTOM_SNOOZE_HOUR);
  const [customSnoozeMinutes, setCustomSnoozeMinutes] = useState(DEFAULT_CUSTOM_SNOOZE_MINUTE);
  const [calendarMonth, setCalendarMonth] = useState(() => getStartOfMonth(new Date()));
  const customSnoozeAnchorRef = useRef<View>(null);
  const minutesInputRef = useRef<TextInput>(null);

  const today = getStartOfDay(new Date());
  const maxDate = getStartOfDay(new Date(Date.now() + MAX_SNOOZE_DAYS_AHEAD * 24 * 60 * 60 * 1000));
  const calendarDays = useMemo(() => getCalendarDaysForMonth(calendarMonth), [calendarMonth]);
  const parsedTime = useMemo(
    () => parseCustomSnoozeTime(customSnoozeHours, customSnoozeMinutes),
    [customSnoozeHours, customSnoozeMinutes]
  );
  const activeSnoozeUntilDate = useMemo(() => {
    if (!snoozeAllUntil) {
      return null;
    }

    const parsedDate = new Date(snoozeAllUntil);
    if (Number.isNaN(parsedDate.getTime()) || parsedDate <= new Date()) {
      return null;
    }

    return parsedDate;
  }, [snoozeAllUntil]);
  const activeSnoozeUntilLabel = useMemo(() => {
    return activeSnoozeUntilDate ? formatSnoozeUntilLabel(activeSnoozeUntilDate) : "";
  }, [activeSnoozeUntilDate]);
  const canConfirmCustomSnooze = customSnoozeDate !== null && parsedTime !== null;
  const hasActiveSnooze = Boolean(activeSnoozeUntilDate);
  const shouldAnimate = animationsEnabled;
  const monthLabel = useMemo(() => {
    return calendarMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  }, [calendarMonth]);

  const enableAnimations = useCallback(() => {
    onUserInteraction();
  }, [onUserInteraction]);

  useEffect(() => {
    if (activeSnoozeUntilDate) {
      setShowCustomSnooze(false);
    }
  }, [activeSnoozeUntilDate]);

  useEffect(() => {
    if (Platform.OS !== "web" || !showCustomSnooze) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const anchorElement = customSnoozeAnchorRef.current as unknown as HTMLElement | null;
      if (!anchorElement || typeof anchorElement.contains !== "function") {
        return;
      }

      if (!anchorElement.contains(event.target as Node)) {
        setShowCustomSnooze(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCustomSnooze]);

  const handleCustomSnoozeToggle = useCallback(() => {
    enableAnimations();
    setShowCustomSnooze((previous) => {
      const next = !previous;
      if (next) {
        const initialDate = customSnoozeDate ?? today;
        setCustomSnoozeDate(initialDate);
        setCalendarMonth(getStartOfMonth(initialDate));
      }
      return next;
    });
  }, [customSnoozeDate, enableAnimations, today]);

  const handleCustomConfirm = useCallback(async () => {
    if (!customSnoozeDate) {
      return;
    }

    if (!parsedTime) {
      showToast({
        message: "Please enter time as HH:mm",
        type: "error",
      });
      return;
    }

    const snoozeDate = new Date(customSnoozeDate);
    snoozeDate.setHours(parsedTime.hours, parsedTime.minutes, 0, 0);

    if (snoozeDate <= new Date()) {
      showToast({
        message: "Please select a time in the future",
        type: "error",
      });
      return;
    }

    enableAnimations();
    const success = await onCustomSnoozeSelect(snoozeDate.toISOString());
    if (!success) {
      return;
    }

    setShowCustomSnooze(false);
    showToast({
      message: `Notifications snoozed until ${snoozeDate.toLocaleString()}`,
      type: "success",
    });
  }, [customSnoozeDate, enableAnimations, onCustomSnoozeSelect, parsedTime, showToast]);

  const handlePresetSnoozePress = useCallback(
    (option: PresetSnoozeOption) => {
      enableAnimations();
      setShowCustomSnooze(false);
      void onPresetSnoozeSelect(option);
    },
    [enableAnimations, onPresetSnoozeSelect]
  );

  const handleResumeSnoozePress = useCallback(() => {
    enableAnimations();
    setShowCustomSnooze(false);
    void onResumeSnooze();
  }, [enableAnimations, onResumeSnooze]);

  const handlePreviousMonth = useCallback(() => {
    setCalendarMonth((previous) => new Date(previous.getFullYear(), previous.getMonth() - 1, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setCalendarMonth((previous) => new Date(previous.getFullYear(), previous.getMonth() + 1, 1));
  }, []);

  const handleCustomSnoozeHoursChange = useCallback((nextValue: string) => {
    const normalizedValue = normalizeTimeSegment(nextValue);
    setCustomSnoozeHours(normalizedValue);

    if (normalizedValue.length === 2) {
      minutesInputRef.current?.focus();
    }
  }, []);

  const handleCustomSnoozeMinutesChange = useCallback((nextValue: string) => {
    const normalizedValue = normalizeTimeSegment(nextValue);
    setCustomSnoozeMinutes(normalizedValue);
  }, []);

  return {
    showCustomSnooze,
    customSnoozeDate,
    customSnoozeHours,
    customSnoozeMinutes,
    calendarMonth,
    monthLabel,
    today,
    maxDate,
    calendarDays,
    canConfirmCustomSnooze,
    hasActiveSnooze,
    shouldAnimate,
    activeSnoozeUntilLabel,
    customSnoozeAnchorRef,
    minutesInputRef,
    setCustomSnoozeDate,
    handleCustomSnoozeToggle,
    handleCustomConfirm,
    handlePresetSnoozePress,
    handleResumeSnoozePress,
    handlePreviousMonth,
    handleNextMonth,
    handleCustomSnoozeHoursChange,
    handleCustomSnoozeMinutesChange,
  };
};
